import { useEffect, useRef, useState } from 'react';
import { WarningCircle, Trash, Flag, Question } from '@phosphor-icons/react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export const CONFIRM = {
    root: 'confirm-dialog',
    title: 'confirm-dialog-title',
    description: 'confirm-dialog-description',
    confirmBtn: 'confirm-dialog-confirm',
    cancelBtn: 'confirm-dialog-cancel',
};

const TONES = {
    danger: {
        Icon: Trash,
        accent: '#FF4D4D',
        confirmClass:
            'bg-[#FF4D4D] text-white hover:bg-[#FF4D4D]/90 active:scale-[0.97]',
    },
    warning: {
        Icon: WarningCircle,
        accent: '#F4A33A',
        confirmClass:
            'bg-[#F4A33A] text-[#121212] hover:bg-[#F4A33A]/90 active:scale-[0.97]',
    },
    primary: {
        Icon: Flag,
        accent: '#00FF9D',
        confirmClass:
            'bg-primary text-primary-foreground shadow-neon hover:-translate-y-[1px] active:scale-[0.97]',
    },
    neutral: {
        Icon: Question,
        accent: undefined,
        confirmClass:
            'bg-foreground text-background hover:opacity-90 active:scale-[0.97]',
    },
};

/**
 * Promise-based confirm dialog — drop-in replacement for window.confirm with
 * GymFlow theming. Use the imperative `useConfirm()` hook below for cleaner
 * call sites:
 *
 *   const confirm = useConfirm();
 *   if (await confirm({ title: 'Delete?', tone: 'danger' })) { ... }
 *
 * Or render <ConfirmDialog /> directly if you want full JSX control.
 */
export function ConfirmDialog({
    open,
    onOpenChange,
    title,
    description,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    tone = 'neutral',
    onConfirm,
}) {
    const t = TONES[tone] || TONES.neutral;
    return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
            <AlertDialogContent
                data-testid={CONFIRM.root}
                className="border border-foreground/10 bg-card rounded-3xl shadow-tactile sm:max-w-md p-7"
            >
                <AlertDialogHeader>
                    <div className="flex items-start gap-3">
                        <span
                            className="grid place-items-center w-10 h-10 rounded-2xl border shrink-0"
                            style={{
                                background: t.accent
                                    ? `${t.accent}1f`
                                    : 'rgba(255,255,255,0.06)',
                                borderColor: t.accent
                                    ? `${t.accent}59`
                                    : 'rgba(255,255,255,0.1)',
                                color: t.accent,
                            }}
                        >
                            <t.Icon size={18} weight="fill" />
                        </span>
                        <div className="min-w-0 flex-1">
                            <AlertDialogTitle
                                data-testid={CONFIRM.title}
                                className="font-display font-extrabold tracking-tight text-xl sm:text-2xl"
                            >
                                {title}
                            </AlertDialogTitle>
                            {description && (
                                <AlertDialogDescription
                                    data-testid={CONFIRM.description}
                                    className="text-sm text-muted-foreground mt-1.5 leading-snug"
                                >
                                    {description}
                                </AlertDialogDescription>
                            )}
                        </div>
                    </div>
                </AlertDialogHeader>
                <AlertDialogFooter className="mt-3 grid grid-cols-2 gap-2 sm:gap-3 sm:flex-row sm:gap-3">
                    <AlertDialogCancel
                        data-testid={CONFIRM.cancelBtn}
                        className="h-11 rounded-2xl border border-foreground/15 bg-foreground/5 hover:bg-foreground/10 text-foreground font-semibold m-0"
                    >
                        {cancelLabel}
                    </AlertDialogCancel>
                    <AlertDialogAction
                        data-testid={CONFIRM.confirmBtn}
                        onClick={onConfirm}
                        className={`h-11 rounded-2xl font-bold tracking-tight transition-all ${t.confirmClass}`}
                    >
                        {confirmLabel}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}

/**
 * Imperative hook — returns a `confirm(opts)` function that resolves to true
 * on confirm and false on cancel. Renders a single ConfirmDialog you mount
 * once per component.
 *
 *   const { confirm, dialog } = useConfirm();
 *   return (<><button onClick={async () => { if (await confirm({title:'Delete?', tone:'danger'})) doDelete(); }}/>{dialog}</>);
 */
export function useConfirm() {
    const [state, setState] = useState({ open: false });
    const resolverRef = useRef(null);

    function confirm(opts = {}) {
        return new Promise((resolve) => {
            resolverRef.current = resolve;
            setState({ open: true, ...opts });
        });
    }

    function handleResolve(ok) {
        const resolver = resolverRef.current;
        resolverRef.current = null;
        setState((s) => ({ ...s, open: false }));
        if (resolver) resolver(ok);
    }

    // Make sure that if the user closes via overlay/escape we resolve false.
    useEffect(() => {
        if (!state.open && resolverRef.current) {
            const resolver = resolverRef.current;
            resolverRef.current = null;
            resolver(false);
        }
    }, [state.open]);

    const dialog = (
        <ConfirmDialog
            open={state.open}
            onOpenChange={(o) => {
                if (!o) handleResolve(false);
            }}
            title={state.title}
            description={state.description}
            confirmLabel={state.confirmLabel}
            cancelLabel={state.cancelLabel}
            tone={state.tone}
            onConfirm={() => handleResolve(true)}
        />
    );

    return { confirm, dialog };
}
